import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { CLASSROOMS_KEY } from '@teetsh/app/src/queries/classrooms/useClassrooms';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { TagWithClassrooms } from '@teetsh/app/src/containers/MesAnneesScolaires/School/Tags/types';
import { TAGS_KEY } from '@teetsh/app/src/queries/tags/useTags';
import { useTranslation } from 'react-i18next';

export interface TagCreateView {
  id: string;
  color: string;
  title: string;
  position: number;
  classroomIds: string[];
}

function createTag({
  schoolyearId,
  schoolId,
  tag,
}: {
  schoolyearId: string;
  schoolId: string;
  tag: TagWithClassrooms;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/tags`;
  const body: TagCreateView = {
    id: tag.id,
    color: tag.color,
    title: tag.title,
    position: tag.position,
    classroomIds: tag.classrooms.map(c => c.id),
  };
  return request(requestURL, {
    method: 'POST',
    body,
  });
}

interface Props {
  schoolyearId: string;
  schoolId: string;
}

export default function useCreateTag(props: Props) {
  const { schoolyearId, schoolId } = props;
  const { notifyError } = useNotification();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['schoolyearSettings']);
  const { mutateAsync } = useMutation({
    mutationFn: (tag: TagWithClassrooms) =>
      createTag({ schoolId, schoolyearId, tag }),
    onMutate: tag => {
      queryClient.setQueryData<TagWithClassrooms[]>(
        [schoolyearId, schoolId, TAGS_KEY],
        data => [tag, ...(data || [])],
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLASSROOMS_KEY],
      });
    },
    onError: () => {
      notifyError({
        title: t('schoolyearSettings:messages.tag.createError'),
      });
      queryClient.invalidateQueries({
        queryKey: [schoolyearId, schoolId, TAGS_KEY],
      });
    },
  });
  return mutateAsync;
}
